// UserAutocomplete — shared picker пользователя (R36b / U-2).
// MUI Autocomplete с живым серверным поиском: GET /api/users/lookup?q=<ввод>
// (debounce 300 мс, filterOptions отключён — фильтрация на сервере).
// Подпись опции: «displayName (email)» (fallback username, если displayName пуст).
// freeSolo=false: выбрать можно только пользователя из выдачи — UUID вручную не вводится (U-2).
// Выбранное значение резолвится через lookupByIds и всегда присутствует в options,
// даже если оно вне текущей поисковой выдачи (R26 / D26: текущий пользователь не теряется).
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Autocomplete, TextField, type TextFieldProps } from '@mui/material';
import { usersApi, type UserLookupDto } from '@/api/users';

/** Debounce живого поиска (мс) — канон CRM-поиска R33c. */
const SEARCH_DEBOUNCE_MS = 300;

/** staleTime результатов поиска: выдача picker'а короткоживущая. */
const LOOKUP_STALE_TIME_MS = 60 * 1000;

/** Подпись опции picker'а: «displayName (email)», fallback — username. */
function getUserOptionLabel(user: UserLookupDto): string {
  const name = user.displayName || user.username;
  return user.email ? `${name} (${user.email})` : name;
}

interface UserAutocompleteProps {
  /** Label поля. */
  label: string;
  /** id выбранного пользователя ('' — не выбран). */
  value: string;
  /** Колбэк выбора: id пользователя или '' при очистке. */
  onChange: (id: string) => void;
  /** Обязательное поле. */
  required?: boolean;
  /** Disabled. */
  disabled?: boolean;
  /** Ошибка валидации формы. */
  error?: boolean;
  /** Текст ошибки/подсказки. */
  helperText?: string | undefined;
  /** ARIA-label для a11y (default = label). */
  'aria-label'?: string | undefined;
}

/**
 * Picker пользователя с живым поиском (U-2).
 * value/onChange — id пользователя; подписи и поиск — внутри компонента.
 */
export function UserAutocomplete({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  error = false,
  helperText,
  'aria-label': ariaLabel,
}: UserAutocompleteProps): JSX.Element {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQ(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Живой поиск (U-2): q-режим lookup; пустой q не шлём (backend требует ровно один режим).
  const searchQuery = useQuery({
    queryKey: ['users-lookup', 'q', debouncedQ],
    queryFn: () => usersApi.lookup(debouncedQ),
    enabled: debouncedQ.length > 0,
    staleTime: LOOKUP_STALE_TIME_MS,
    retry: false,
  });

  // Резолв выбранного id → DTO (подпись + гарантированное присутствие в options).
  const selectedQuery = useQuery({
    queryKey: ['users-lookup', 'ids', [value]],
    queryFn: () => usersApi.lookupByIds([value]),
    enabled: value.length > 0,
    staleTime: LOOKUP_STALE_TIME_MS,
    retry: false,
  });
  const selectedUser = selectedQuery.data?.find((u) => u.id === value) ?? null;

  // FIX-C (MEDIUM S4): ошибки поиска/резолва не проглатывались — пустой список без признака.
  // isError поиска → noOptionsText «Ошибка загрузки»; isError резолва выбранного →
  // сообщение в helperText (флаг error подсвечивает поле).
  const selectedResolveFailed = selectedQuery.isError;
  const effectiveError = error || selectedResolveFailed;
  const effectiveHelperText = selectedResolveFailed
    ? 'Не удалось загрузить выбранного пользователя'
    : helperText;

  const options = useMemo(() => {
    const found = searchQuery.data ?? [];
    if (selectedUser && !found.some((u) => u.id === selectedUser.id)) {
      return [selectedUser, ...found];
    }
    return found;
  }, [searchQuery.data, selectedUser]);

  return (
    <Autocomplete<UserLookupDto, false, false, false>
      options={options}
      value={selectedUser}
      onChange={(_, option) => onChange(option?.id ?? '')}
      onInputChange={(_, newInput, reason) => {
        // Ищем только по ручному вводу; 'reset' (выбор опции) поиск не триггерит.
        if (reason === 'input') setSearchInput(newInput);
        if (reason === 'clear') setSearchInput('');
      }}
      getOptionLabel={getUserOptionLabel}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      // Фильтрация на сервере (lookup ILIKE) — клиентский фильтр отключён.
      filterOptions={(x) => x}
      openOnFocus
      disabled={disabled}
      loading={searchQuery.isFetching}
      noOptionsText={searchQuery.isError ? 'Ошибка загрузки' : 'Начните вводить имя или email'}
      renderInput={(params) => (
        <TextField
          // AutocompleteRenderInputParams несёт явные undefined (size и др.), что под
          // exactOptionalPropertyTypes несовместимо с TextFieldProps — точечный cast.
          {...(params as unknown as TextFieldProps)}
          label={label}
          required={required}
          error={effectiveError}
          // exactOptionalPropertyTypes: undefined не передаём явно.
          {...(effectiveHelperText !== undefined ? { helperText: effectiveHelperText } : {})}
          inputProps={{ ...params.inputProps, 'aria-label': ariaLabel ?? label }}
        />
      )}
    />
  );
}
